import type { LtiConsumedToolDeployment } from '@efdigital/ll-admin-v2/data/types/lti';
import type { LibraryClientsEditLtiIntegrationsPlatformModel } from '@efdigital/ll-admin-v2/routes/library/clients/client/edit/lti-integrations/platform';
import {
  findXceedTool,
  XCEED_PRODUCT,
} from '@efdigital/ll-admin-v2/utils/lti/products/xceed';
import {
  deploymentPhaseStatus,
  phaseStatusIcon,
  phaseStatusIconClass,
  platformPhaseStatus,
  toolPhaseStatus,
} from '@efdigital/ll-admin-v2/utils/lti/setup-status';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';

interface Signature {
  Args: {
    model: LibraryClientsEditLtiIntegrationsPlatformModel;
  };
}

export default class LtiIntegrationsPlatformTemplate extends Component<Signature> {
  get xceedTool() {
    return findXceedTool(this.args.model.tools);
  }

  get xceedDeployments(): LtiConsumedToolDeployment[] {
    if (!this.xceedTool) {
      return [];
    }

    return this.args.model.deploymentsByToolId[this.xceedTool.id] ?? [];
  }

  get platformPhaseStatusValue() {
    return platformPhaseStatus(this.args.model.platform);
  }

  get toolPhaseStatusValue() {
    return toolPhaseStatus(this.xceedTool);
  }

  get deploymentPhaseStatusValue() {
    return deploymentPhaseStatus(this.xceedDeployments);
  }

  get deploymentPhaseIcon() {
    const status = this.deploymentPhaseStatusValue;
    if (status === 'complete') {
      return phaseStatusIcon('complete');
    }
    if (status === 'partial') {
      return phaseStatusIcon('awaiting_customer');
    }
    return phaseStatusIcon('incomplete');
  }

  get deploymentPhaseIconClass() {
    const status = this.deploymentPhaseStatusValue;
    if (status === 'complete') {
      return phaseStatusIconClass('complete');
    }
    if (status === 'partial') {
      return phaseStatusIconClass('awaiting_customer');
    }
    return phaseStatusIconClass('incomplete');
  }

  <template>
    <div
      class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4"
    >
      <div>
        <LinkTo
          @route="library.clients.client.edit.lti-integrations.index"
          class="btn btn-sm btn-outline-primary mb-3"
        >
          <iconify-icon inline icon="ph:arrow-left" />
          Back to platforms
        </LinkTo>
        <h2 class="h4 mb-1">{{@model.platform.name}}</h2>
        {{#if @model.platform.description}}
          <p class="text-muted mb-0">{{@model.platform.description}}</p>
        {{/if}}
      </div>
    </div>

    <nav
      class="nav nav-tabs flex-column flex-sm-row mb-4"
      data-test-lti-phase-nav
    >
      <LinkTo
        @route="library.clients.client.edit.lti-integrations.platform.index"
        class="nav-link d-flex align-items-center gap-2 fw-semibold"
      >
        <iconify-icon
          inline
          icon={{phaseStatusIcon this.platformPhaseStatusValue}}
          class={{phaseStatusIconClass this.platformPhaseStatusValue}}
        />
        Phase 1: Register the LMS platform
      </LinkTo>
      <LinkTo
        @route="library.clients.client.edit.lti-integrations.platform.tool"
        class="nav-link d-flex align-items-center gap-2 fw-semibold"
      >
        <iconify-icon
          inline
          icon={{phaseStatusIcon this.toolPhaseStatusValue}}
          class={{phaseStatusIconClass this.toolPhaseStatusValue}}
        />
        Phase 2:
        {{XCEED_PRODUCT.phase2Title}}
      </LinkTo>
      <LinkTo
        @route="library.clients.client.edit.lti-integrations.platform.deployments"
        class="nav-link d-flex align-items-center gap-2 fw-semibold"
      >
        <iconify-icon
          inline
          icon={{this.deploymentPhaseIcon}}
          class={{this.deploymentPhaseIconClass}}
        />
        Phase 3: Tool deployment
      </LinkTo>
    </nav>

    {{outlet}}
  </template>
}
